import {
  FilterSegment, FilterField, Dependencies, BoxDependency,
} from '@/types/flow-types';
import { TypeCampaignEnum } from '@/types';
import type { FlowStructureDto, FlowStepDto } from '@/types/flow-structure';
import { parseOperatorParameters } from '@/helpers/flowParameters';
import { predefinedEvents } from '@/composables/events/constants';

export interface ActiveSegment {
  name: string;
  label: string;
  number?: number;
  show: boolean;
}

export interface FlowDependencyContext {
  flow: FlowStructureDto | null;
  currentStepId: string | null;
  /** Direct parent operator types (used when adding new operator, before it has a stepId) */
  directParentTypes?: string[];
  /** All ancestor operator types (used when adding new operator, before it has a stepId) */
  allAncestorTypes?: string[];
}

// Filter operator IDs that contain segments (inclusion/exclusion)
const FILTER_OPERATOR_IDS = [
  'boxfiltre_1', // Customer
  'boxfiltre_2', // CurrentCart
  'boxfiltre_3', // CurrentOrder
  'boxfiltre_4', // PurchaseHistory
  'boxnavigationtrigger', // NavigationHistory
];

/**
 * Get direct parent step IDs for a given step
 */
const getDirectParentIds = (flow: FlowStructureDto, stepId: string): string[] => {
  const step = flow.steps.find((s) => s.id === stepId);
  if (!step) return [];
  return step.parents.map((p) => p.id);
};

/**
 * Get all parent step IDs recursively up to root
 */
const getAllParentIds = (flow: FlowStructureDto, stepId: string): string[] => {
  const allParents: string[] = [];
  const visited = new Set<string>();

  const traverse = (currentId: string) => {
    const step = flow.steps.find((s) => s.id === currentId);
    if (!step || visited.has(currentId)) return;
    visited.add(currentId);

    step.parents.forEach((parent) => {
      if (!allParents.includes(parent.id)) {
        allParents.push(parent.id);
      }
      traverse(parent.id);
    });
  };

  traverse(stepId);
  return allParents;
};

/**
 * Get operator ID (box type) from step parameters
 */
const getOperatorIdFromStep = (step: FlowStepDto): string => step.parameters?.id ?? step.type ?? '';

/**
 * Get the effective operator ID for dependency checking
 * For triggers with events (like boxtriggerinternalevent), returns the subcomponent ID
 */
const getEffectiveOperatorId = (step: FlowStepDto): string => {
  const operatorId = getOperatorIdFromStep(step);
  const params = parseOperatorParameters(step.parameters);

  // Check if this is a trigger with an event that maps to a subcomponent
  if (params.event && typeof params.event === 'string' && params.event in predefinedEvents) {
    return predefinedEvents[params.event].subcomponent;
  }

  return operatorId;
};

/**
 * Check if operator dependencies are satisfied
 */
const satisfyOperatorsDependencies = (
  dependencies: { direct_parent: (string | BoxDependency)[]; any_parents: (string | BoxDependency)[] },
  flow: FlowStructureDto,
  directParentIds: string[],
  allParentIds: string[],
): boolean => {
  let result = false;

  // Build parent info with effective operator IDs (handles event subcomponents)
  const buildParentInfo = (parentIds: string[]) => parentIds.map((id) => {
    const step = flow.steps.find((s) => s.id === id);
    if (!step) return { stepId: id, operatorId: '' };
    const operatorId = getEffectiveOperatorId(step);
    return { stepId: id, operatorId };
  });

  const directParents = buildParentInfo(directParentIds);
  const allParents = buildParentInfo(allParentIds);

  // Check direct parent dependencies
  if (dependencies.direct_parent.length) {
    result = dependencies.direct_parent.some((dependency) => {
      const boxId = typeof dependency === 'string' ? dependency : dependency.boxId;
      const matched = directParents.filter((p) => p.operatorId === boxId);

      if (!matched.length) return false;

      if (typeof dependency === 'string') {
        return true;
      }

      // Check attribute value
      return matched.some((parent) => {
        const step = flow.steps.find((s) => s.id === parent.stepId);
        if (!step) return false;
        const params = parseOperatorParameters(step.parameters);
        return params[dependency.attribute] === dependency.value;
      });
    });
  }

  // Check any parent dependencies
  if (!result && dependencies.any_parents.length) {
    result = dependencies.any_parents.some((dependency) => {
      const boxId = typeof dependency === 'string' ? dependency : dependency.boxId;
      const matched = allParents.filter((p) => p.operatorId === boxId);

      if (!matched.length) return false;

      if (typeof dependency === 'string') {
        return true;
      }

      // Check attribute value
      return matched.some((parent) => {
        const step = flow.steps.find((s) => s.id === parent.stepId);
        if (!step) return false;
        const params = parseOperatorParameters(step.parameters);
        return params[dependency.attribute] === dependency.value;
      });
    });
  }

  return result;
};

/**
 * Check if segment dependencies are satisfied
 */
const satisfySegmentsDependencies = (
  dependencies: { direct_parents_contain: string[]; any_parents_contain: string[] },
  flow: FlowStructureDto,
  directParentIds: string[],
  allParentIds: string[],
  currentActiveSegments: string[] = [],
): boolean => {
  let result = false;

  // Get segments from filter operators
  const getSegmentsFromStep = (step: FlowStepDto): string[] => {
    const operatorId = getOperatorIdFromStep(step);
    if (!FILTER_OPERATOR_IDS.includes(operatorId)) return [];

    const params = parseOperatorParameters(step.parameters);
    const segments: string[] = [];

    if (params.inclusion && typeof params.inclusion === 'object') {
      segments.push(...Object.keys(params.inclusion));
    }
    if (params.exclusion && typeof params.exclusion === 'object') {
      segments.push(...Object.keys(params.exclusion));
    }

    return segments;
  };

  // Check direct parent segment dependencies
  if (dependencies.direct_parents_contain.length) {
    const parentSegments = directParentIds
      .map((id) => flow.steps.find((s) => s.id === id))
      .filter((step): step is FlowStepDto => !!step)
      .filter((step) => FILTER_OPERATOR_IDS.includes(getOperatorIdFromStep(step)))
      .flatMap(getSegmentsFromStep);

    const allSegments = [...currentActiveSegments, ...parentSegments];
    result = dependencies.direct_parents_contain.some((seg) => allSegments.includes(seg));
  }

  // Check any parent segment dependencies
  if (!result && dependencies.any_parents_contain.length) {
    const parentSegments = allParentIds
      .map((id) => flow.steps.find((s) => s.id === id))
      .filter((step): step is FlowStepDto => !!step)
      .filter((step) => FILTER_OPERATOR_IDS.includes(getOperatorIdFromStep(step)))
      .flatMap(getSegmentsFromStep);

    result = dependencies.any_parents_contain.some((seg) => parentSegments.includes(seg));
  }

  return result;
};

/**
 * Format dependencies for a given campaign type
 */
const getFormattedDependencies = (typeCampaign: TypeCampaignEnum, dependencies: Dependencies | null | undefined) => {
  const campaignDeps = dependencies?.[typeCampaign];
  const operatorsDeps = campaignDeps?.operators;
  const segmentsDeps = campaignDeps?.segments;

  return {
    operatorsDependencies: {
      direct_parent: operatorsDeps?.direct_parents ?? [],
      any_parents: operatorsDeps?.any_parents ?? [],
    },
    segmentsDependencies: {
      direct_parents_contain: segmentsDeps?.direct_parents_contain ?? [],
      any_parents_contain: segmentsDeps?.any_parents_contain ?? [],
    },
  };
};

/**
 * Check if a segment satisfies its dependencies
 */
const satisfyDependencies = (
  dependencies: Dependencies | null | undefined,
  typeCampaign: TypeCampaignEnum,
  flow: FlowStructureDto,
  directParentIds: string[],
  allParentIds: string[],
  currentActiveSegments: string[] = [],
): boolean => {
  if (!dependencies) return true;

  const { operatorsDependencies, segmentsDependencies } = getFormattedDependencies(typeCampaign, dependencies);

  const hasOperatorsDeps = operatorsDependencies.direct_parent.length > 0 || operatorsDependencies.any_parents.length > 0;
  const hasSegmentsDeps = segmentsDependencies.direct_parents_contain.length > 0 || segmentsDependencies.any_parents_contain.length > 0;

  if (!hasOperatorsDeps && !hasSegmentsDeps) return true;

  if (hasOperatorsDeps && hasSegmentsDeps) {
    return satisfyOperatorsDependencies(operatorsDependencies, flow, directParentIds, allParentIds)
      || satisfySegmentsDependencies(segmentsDependencies, flow, directParentIds, allParentIds, currentActiveSegments);
  }

  if (hasOperatorsDeps) {
    return satisfyOperatorsDependencies(operatorsDependencies, flow, directParentIds, allParentIds);
  }

  return satisfySegmentsDependencies(segmentsDependencies, flow, directParentIds, allParentIds, currentActiveSegments);
};

/**
 * Check if operator dependencies are satisfied using operator types directly
 * (for new operators that don't have a stepId yet)
 */
const satisfyOperatorsDependenciesFromTypes = (
  dependencies: { direct_parent: (string | BoxDependency)[]; any_parents: (string | BoxDependency)[] },
  directParentTypes: string[],
  allAncestorTypes: string[],
): boolean => {
  let result = false;

  // Check direct parent dependencies (simple string match only, no attribute checking)
  if (dependencies.direct_parent.length) {
    result = dependencies.direct_parent.some((dependency) => {
      const boxId = typeof dependency === 'string' ? dependency : dependency.boxId;
      return directParentTypes.includes(boxId);
    });
  }

  // Check any parent dependencies
  if (!result && dependencies.any_parents.length) {
    result = dependencies.any_parents.some((dependency) => {
      const boxId = typeof dependency === 'string' ? dependency : dependency.boxId;
      return allAncestorTypes.includes(boxId);
    });
  }

  return result;
};

/**
 * Check if segment dependencies are satisfied using operator types directly
 * (for new operators that don't have a stepId yet)
 */
const satisfySegmentsDependenciesFromTypes = (
  dependencies: { direct_parents_contain: string[]; any_parents_contain: string[] },
  flow: FlowStructureDto,
  directParentTypes: string[],
  allAncestorTypes: string[],
  currentActiveSegments: string[] = [],
): boolean => {
  // For segment dependencies, we need to check if parent filters contain specific segments
  // Since we're adding a new operator, we check segments in existing parent filter operators

  // Get segments from filter operators by type
  const getSegmentsFromParentFilters = (parentTypes: string[]): string[] => {
    const segments: string[] = [];
    // Find all steps that match the parent types and are filter operators
    flow.steps
      .filter((step) => {
        const operatorId = getOperatorIdFromStep(step);
        return parentTypes.includes(operatorId) && FILTER_OPERATOR_IDS.includes(operatorId);
      })
      .forEach((step) => {
        const params = parseOperatorParameters(step.parameters);
        if (params.inclusion && typeof params.inclusion === 'object') {
          segments.push(...Object.keys(params.inclusion));
        }
        if (params.exclusion && typeof params.exclusion === 'object') {
          segments.push(...Object.keys(params.exclusion));
        }
      });
    return segments;
  };

  let result = false;

  // Check direct parent segment dependencies
  if (dependencies.direct_parents_contain.length) {
    const parentSegments = getSegmentsFromParentFilters(directParentTypes);
    const allSegments = [...currentActiveSegments, ...parentSegments];
    result = dependencies.direct_parents_contain.some((seg) => allSegments.includes(seg));
  }

  // Check any parent segment dependencies
  if (!result && dependencies.any_parents_contain.length) {
    const parentSegments = getSegmentsFromParentFilters(allAncestorTypes);
    result = dependencies.any_parents_contain.some((seg) => parentSegments.includes(seg));
  }

  return result;
};

/**
 * Check if a segment satisfies its dependencies using operator types directly
 * (for new operators that don't have a stepId yet)
 */
const satisfyDependenciesFromTypes = (
  dependencies: Dependencies | null | undefined,
  typeCampaign: TypeCampaignEnum,
  directParentTypes: string[],
  allAncestorTypes: string[],
  currentActiveSegments: string[] = [],
  flow: FlowStructureDto,
): boolean => {
  if (!dependencies) return true;

  const { operatorsDependencies, segmentsDependencies } = getFormattedDependencies(typeCampaign, dependencies);

  const hasOperatorsDeps = operatorsDependencies.direct_parent.length > 0 || operatorsDependencies.any_parents.length > 0;
  const hasSegmentsDeps = segmentsDependencies.direct_parents_contain.length > 0 || segmentsDependencies.any_parents_contain.length > 0;

  if (!hasOperatorsDeps && !hasSegmentsDeps) return true;

  if (hasOperatorsDeps && hasSegmentsDeps) {
    return satisfyOperatorsDependenciesFromTypes(operatorsDependencies, directParentTypes, allAncestorTypes)
      || satisfySegmentsDependenciesFromTypes(segmentsDependencies, flow, directParentTypes, allAncestorTypes, currentActiveSegments);
  }

  if (hasOperatorsDeps) {
    return satisfyOperatorsDependenciesFromTypes(operatorsDependencies, directParentTypes, allAncestorTypes);
  }

  return satisfySegmentsDependenciesFromTypes(segmentsDependencies, flow, directParentTypes, allAncestorTypes, currentActiveSegments);
};

/**
 * Get available segments for flow editor with dependency checking
 */
export const getAvailableSegmentsForFlow = <T>(
  typeCampaign: TypeCampaignEnum,
  segments: FilterSegment<T>[],
  currentActiveSegments: string[],
  context?: FlowDependencyContext,
): FilterSegment<T>[] => {
  // Filter by campaign type first
  let filtered = segments.filter((segment: FilterSegment<T>) => segment.availableInCampaign.includes(typeCampaign));

  // If we have flow context with a valid step, also check dependencies
  if (context?.flow && context?.currentStepId) {
    const directParentIds = getDirectParentIds(context.flow, context.currentStepId);
    const allParentIds = getAllParentIds(context.flow, context.currentStepId);

    // Only filter by dependencies if we have parents to check against
    if (directParentIds.length > 0 || allParentIds.length > 0) {
      filtered = filtered.filter((segment: FilterSegment<T>) => satisfyDependencies(
        segment.dependencies,
        typeCampaign,
          context.flow!,
          directParentIds,
          allParentIds,
          currentActiveSegments,
      ));
    }
  } else if (context?.flow && context?.allAncestorTypes) {
    // For new operators being added: use ancestor types directly from insert context
    // This allows dependency checking before the operator has a stepId
    const directParentTypes = context.directParentTypes ?? [];
    const allAncestorTypes = context.allAncestorTypes ?? [];

    if (directParentTypes.length > 0 || allAncestorTypes.length > 0) {
      filtered = filtered.filter((segment: FilterSegment<T>) => satisfyDependenciesFromTypes(
        segment.dependencies,
        typeCampaign,
        directParentTypes,
        allAncestorTypes,
        currentActiveSegments,
        context.flow!,
      ));
    }
  }

  return filtered;
};

export const addInArraySegment = <T>(segment: FilterSegment<T>, filter: any|undefined) => {
  let updatedFilter = { ...filter };
  if (!filter) {
    updatedFilter = {
      segment_number: 0,
      litteral_title: {},
      ...segment.fields.reduce((acc, field) => ({ ...acc, [field.id]: [] }), {}),
    };
  }
  if (segment.fields) {
    segment.fields.forEach((field: FilterField) => {
      updatedFilter = {
        ...updatedFilter,
        [field.id]: [
          ...updatedFilter[field.id],
          field.data,
        ],
        litteral_title: {
          ...updatedFilter.litteral_title,
          [field.id]: field.litteral_title,
        },
      };
      updatedFilter.litteral_title[field.id] = field.litteral_title;
    });
  }
  updatedFilter.segment_number += 1;
  return updatedFilter;
};

export const addInSegment = <T>(segment: FilterSegment<T>, filter: any) => {
  let updatedFilter = { ...filter };
  if (!filter) {
    updatedFilter = {
      segment_number: 0,
      litteral_title: {},
      ...segment.fields.reduce((acc, field) => ({ ...acc, [field.id]: [] }), {}),
    };
  }
  if (segment.fields) {
    segment.fields.forEach((field: FilterField) => {
      updatedFilter = {
        ...updatedFilter,
        [field.id]: field.data,
        litteral_title: {
          ...updatedFilter.litteral_title,
          [field.id]: field.litteral_title,
        },
      };
      updatedFilter.litteral_title[field.id] = field.litteral_title;
    });
  }
  updatedFilter.segment_number += 1;
  return updatedFilter;
};

export const removeInSegment = <T>(segments: any, segmentName: keyof T, number: number) => {
  let segmentToUpdate: any = {};
  if (segments[segmentName].segment_number === 1) { // delete entire segment if number is 1
    // eslint-disable-next-line no-param-reassign
    delete segments[segmentName];
  } else {
    segmentToUpdate = {
      segment_number: segments[segmentName].segment_number - 1,
      litteral_title: segments[segmentName].litteral_title,
    };
    Object.keys(segments[segmentName])
      .filter((key: string) => !['segment_number', 'litteral_title'].includes(key)) // get keys to update in object
      .forEach((key: string) => { // for each field
        segmentToUpdate[key] = [...segments[segmentName][key]];
        segmentToUpdate[key].splice(number, 1); // remove index
      });
    // eslint-disable-next-line no-param-reassign
    segments = {
      ...segments,
      [segmentName]: segmentToUpdate,
    };
  }
  return segments;
};

export const getActiveSegments = <T>(filters: T, segments: FilterSegment<T>[]) => (Object.entries(filters)
  .reduce((acc: ActiveSegment[], element: any[]) => {
    const activeSegments = [];
    for (let i = 0; i < element[1].segment_number; i++) {
      const segment: FilterSegment<T> = segments.filter((curent: FilterSegment<T>) => curent.id === element[0])[0];
      if (segment) {
        activeSegments.push({
          name: element[0], label: segment.option.label, number: i, show: segment.fields.filter((field) => !field.hidden).length > 0,
        });
      }
    }
    acc.push(...activeSegments);
    return acc;
  }, []));
